/**
 * §13.1.2 Loadout — the pre-run screen, rebuilt around what the seed actually decides.
 *
 * ## What changed and why
 *
 * The screen used to be five controls in a column: a starter picker, four carousels and a
 * seed field. Everything on it was a *setting*, and the seed field — the one input that
 * decides who you are, what you fly and what the run does — was a number in the footer with
 * nothing on screen to show for it.
 *
 * Two systems landed that make that arrangement wrong. AMD-42 turned `runSeed` into a
 * captain with a face, a name and a temperament. AMD-43 and AMD-50 turned it into a HULL —
 * one closed outline, 20,000 verified-unique variants from the seed, reshaped by the twelve
 * trait points the build derives. So the seed is now the loudest thing on this screen and it
 * was being shown as four digits.
 *
 * The screen therefore opens with the two things the seed makes: **YOUR CAPTAIN** and
 * **YOUR SHIP**, both drawn, both live to the seed field. Change the seed and both change.
 * Under them sit the two choices that are actually yours to make — the starting weapon and
 * the trait vector it produces — and under those the four run settings that were always
 * settings. The footer keeps the seed and launches.
 *
 * ## The starter picker is still not a carousel (§9.9.1)
 *
 * All three starters are unlocked from the first run, the choice is between them rather than
 * along them, and the previous complaint about this screen was that carousel arrows read as
 * decoration. So the three are cards, all visible, all tappable, stacked full-width in their
 * own column.
 *
 * The harder problem is that the three are *balanced*: §9.9.1 puts all of them at about
 * 10.5 DPS at level 1 on purpose, and a player who suspects one of them is secretly the
 * strong pick will spend the run second-guessing the opening instead of playing it. The
 * picker prints the same damage figure on all three cards, in the same place, in the same
 * tone. What the cards differ on is the verb — kite, charge, ram — and the projectile bill,
 * which is a difference in demand rather than in strength and therefore reinforces the parity
 * claim instead of undercutting it. The hull each one produces is shown once, full size, in
 * YOUR SHIP, rather than three times at 56 px where two of the three are indistinguishable.
 *
 * ## Trait points are shown, not spent
 *
 * AMD-50 derives all twelve from the build you earn during the run — `recomputeStats`
 * re-derives them from what you own, every time, so pick order cannot change them and a
 * snapshot restore reproduces them for free. The traits column here is therefore a READOUT
 * of what the chosen starter allocates, with the baseline marked, and it says so. It is not
 * a stepper wearing a disabled state.
 */

import { DIFFICULTY_TIERS, SHIP_FORM, weaponById } from '../../core/content';
import { parseSeed } from '../../core/rng';
import { makeCaptain, type Captain } from '../../captain/seed';
import { CARD_SCALE, composePortrait, paintPortraitToCanvas, recompose, type Portrait } from '../../captain/portrait';
import {
  TRAIT_ATTACK,
  TRAIT_BASELINE,
  TRAIT_DEFENCE,
  TRAIT_PCT_PER_POINT,
  TRAIT_SPEED,
  TRAIT_TOTAL_POINTS,
} from '../../sim/build';
import { newWeaponDelta, STARTER_WEAPONS } from '../../sim/progression';
import { button, div, el, icon, setClass, setHidden, setText, span, svgTag } from '../dom';
import { formatSeed, humanizeId, parseShareString } from '../format';
import { HullPreview, starterShipForm, starterTraitPoints } from '../hull-preview';
import { ACTIVE_ICONS, DIFFICULTY_ICONS, iconFor, LOADOUT_ROW_ICONS, POLICY_ICONS } from '../icons';
import { TARGETING_POLICIES } from '../settings-store';
import type { LoadoutSelection } from '../types';
import { Screen, type ActionSink } from './base';

/** Arena silhouettes, drawn from the §10.8 geometry column rather than art. */
type ArenaPlan = 'open' | 'chimneys' | 'ridges';

interface Option {
  id: string;
  label: string;
  detail: string;
  locked: boolean;
  unlock_text: string;
  /** Sprite glyph for the option itself; empty where a silhouette carries it. */
  glyph: string;
  plan?: ArenaPlan;
}

/**
 * §10.8 arena variants (3). There is no `arenas.json` in `src/content/`, so the table
 * is transcribed here: name, hazard summary and unlock gate all come from §10.8, and
 * the unlock lines name the §9.9 achievement plus its condition in plain language.
 */
const ARENAS: readonly Option[] = [
  {
    id: 'arena.trench-floor',
    label: 'Trench Floor',
    detail: 'Open kiting, no cover. Vents at 8:00, brine-fall 13:00, tide surge 16:30.',
    locked: false,
    unlock_text: '',
    glyph: '',
    plan: 'open',
  },
  {
    id: 'arena.smoker-gardens',
    label: 'Smoker Gardens',
    detail: 'Nine chimneys break formations. Vent emitters ×1.5, denser denial zones.',
    locked: true,
    unlock_text: 'ach.deep-victory — first victory (kill the Hadal Sovereign)',
    glyph: '',
    plan: 'chimneys',
  },
  {
    id: 'arena.hadal-gate',
    label: 'Hadal Gate',
    detail: 'Two ridge walls, three gaps each. No vents; the tide surge funnels through them.',
    locked: true,
    unlock_text: 'ach.descent-1-clear — win a run on Descent I',
    glyph: '',
    plan: 'ridges',
  },
];

/** §13.1.2 active slot: Dash plus unlock-gated alternatives, max 1 (§9.9). */
const ACTIVES: readonly Option[] = [
  {
    id: 'active.dash',
    label: 'Dash',
    detail: 'Short burst with 0.25 s of invulnerability. 2 s cooldown.',
    locked: false,
    unlock_text: '',
    glyph: ACTIVE_ICONS['active.dash'],
  },
  {
    id: 'weapon.gulper-maw',
    label: 'Gulper Maw',
    detail: 'Lunge that swallows what it touches. Replaces Dash for the run.',
    locked: true,
    unlock_text: 'ach.maw-instinct — 250 dashes (lifetime)',
    glyph: ACTIVE_ICONS['weapon.gulper-maw'],
  },
];

/**
 * §9.9.1 — what the three starters ask of the pilot.
 *
 * The mechanism sentence is not here on purpose: `newWeaponDelta(id, 1)` already owns it
 * and the sim grants exactly what that sentence describes, so re-writing it in the UI is
 * how the two drift apart. What lives here is the part the sim has no sentence for — the
 * demand the weapon makes on the person holding it, and the bill it hands the projectile
 * budget. `verb` is one word because the row is read at a glance; `shape` is the sentence
 * you read once you have stopped on a card.
 *
 * `aim` / `reach` / `cost` are transcribed from `weapons.json` (`targeting_policy`,
 * `base_stats.range_u` / `area`, `perf_cost.projectiles_per_s`) rather than computed:
 * the honest reading of "12 pellets a volley" is a level-5 fact and a level-1 volley is
 * 5, so a derived figure would either be wrong or need a caveat longer than the card.
 */
interface Starter {
  id: string;
  verb: string;
  aim: string;
  reach: string;
  cost: string;
  shape: string;
}

const STARTERS: readonly Starter[] = [
  {
    id: 'weapon.spear-jelly-lance',
    verb: 'Kite',
    aim: 'aims itself',
    reach: '62 u',
    cost: '1 bolt/s',
    shape: 'Backs off and picks. Longest reach, fewest bodies on the field — the clean carrier for anything that splits a bolt.',
  },
  {
    id: 'weapon.krill-scatter',
    verb: 'Charge',
    aim: 'where you point',
    reach: '22 u cone',
    cost: '12 pellets/volley',
    shape: 'Fires where your nose points and nowhere else, out to 22 u. You have to drive into the pack, and you pay for it in projectiles.',
  },
  {
    id: 'weapon.copepod-halo',
    verb: 'Ram',
    aim: 'never aims',
    reach: '6 u ring',
    cost: 'spawns nothing',
    shape: 'No target, no trigger, never off. You kill by putting your hull where the enemies are — and no boon that works on bolts touches it.',
  },
];

/**
 * The one figure that is the same on all three cards, and the reason it is printed.
 *
 * §9.9.1's acceptance criteria pin every starter at or above ~10.5 DPS against a dummy
 * at level 1. Rounding them to one shared figure rather than printing 10.8 / 10.5 / 10.0
 * is deliberate: three near-identical numerals invite exactly the comparison the design
 * does not want, where three identical ones close it.
 */
const STARTER_DPS = '≈10.5 dps';

/**
 * The three traits, in `ShipForm.points` order.
 *
 * The names are the loudest thing about a trait row, so they are verbs-as-nouns rather than
 * stat labels: `speed` / `defence` / `attack` describe the arithmetic, `SWIFT` / `BULWARK` /
 * `STRIKER` describe the ship. The `hull` word is what that trait does to the SILHOUETTE,
 * which is the whole reason the row sits next to a drawing — a player who reads "bulwark →
 * barge" and then looks at the preview has been told the same thing twice, once in a word
 * and once in a shape, and that is how a mechanic stops being a number.
 *
 * `stat` is transcribed from `SHIP_FORM.point_buy.stat_by_trait`; the per-point magnitude is
 * `TRAIT_PCT_PER_POINT` and is read, never restated.
 */
interface TraitRow {
  index: number;
  label: string;
  glyph: string;
  hull: string;
  stat: string;
}

const TRAITS: readonly TraitRow[] = [
  { index: TRAIT_SPEED, label: 'Swift', glyph: 'bolt', hull: 'a dart', stat: 'move speed' },
  { index: TRAIT_DEFENCE, label: 'Bulwark', glyph: 'heart', hull: 'a barge', stat: 'max hp' },
  { index: TRAIT_ATTACK, label: 'Striker', glyph: 'crosshair', hull: 'a blade', stat: 'damage' },
];

/**
 * Pips per trait row. Eight, not twelve: a trait can in principle take the whole pool, but
 * the reachable range over every weapon and passive in `ship-form.json` is roughly 2–8, and
 * a twelve-pip row would spend a third of its width on states nothing can produce. The
 * baseline sits on pip four, marked, so a row reads as a distance from 4/4/4 rather than as
 * an absolute the player has no scale for.
 */
const TRAIT_PIPS = 8;

/** The hull word each dominant trait produces. Mirrors `TRAITS[].hull`, singular. */
const HULL_CLASS: readonly string[] = ['DART', 'BARGE', 'BLADE'];

/**
 * §9.9.1 — three cards, all visible, one selected, each wearing its own ship.
 *
 * A radiogroup rather than a carousel (see the module header). The group is one nav stop
 * that steps left/right like the carousels do, so a gamepad reaches the same three cards
 * with the same gesture; each card is also a real button, so touch and mouse hit it
 * directly without ever meeting an arrow.
 */
class StarterPicker {
  readonly root: HTMLDivElement;
  private cards: HTMLButtonElement[] = [];
  private index = 0;
  private detail: HTMLElement;
  private shape: HTMLElement;
  private aim: HTMLElement;
  private reach: HTMLElement;
  private cost: HTMLElement;
  private seed = 0;
  private onChange: () => void;

  constructor(onChange: () => void) {
    this.onChange = onChange;
    this.root = div('starter-pick');
    this.root.dataset.navHorizontal = 'true';
    this.root.tabIndex = 0;
    this.root.setAttribute('role', 'radiogroup');

    const head = div('col-head');
    head.appendChild(icon(LOADOUT_ROW_ICONS.starter, 'carousel-emblem'));
    head.appendChild(div('carousel-label', 'Weapon · Slot 0'));
    // The parity claim, stated once above the row and then again on every card. Above
    // the row it is a rule; on the cards it is evidence.
    head.appendChild(span('starter-parity', 'all ' + STARTER_DPS + ' at Lv 1'));
    this.root.appendChild(head);

    const cards = div('starter-cards');
    for (let i = 0; i < STARTERS.length; i++) {
      const s = STARTERS[i];
      const def = weaponById.get(s.id);
      const name = def === undefined ? humanizeId(s.id) : def.display_name;
      const card = el('button', 'starter-card');
      card.type = 'button';
      card.setAttribute('role', 'radio');
      card.dataset.value = s.id;

      // The art slot carries the WEAPON's glyph. It used to carry the hull, on the argument
      // that the difference between these three is a difference in ship — but AMD-50 measured
      // that difference and two of the three are siblings (Lance ↔ Krillscatter 0.091 against
      // the 0.162 a seed change moves), so at 56 px the row drew the same pale wedge three
      // times and the picture said nothing the tags did not. The selected hull is already on
      // this screen, full size, in YOUR SHIP, and it updates on every pick. So the glyph is
      // promoted back to being the picture: it is the mark the HUD build strip carries for the
      // rest of the run, and it is the one thing on the row that differs at a glance.
      const art = div('starter-art');
      art.appendChild(icon(iconFor(s.id, 'weapon'), 'starter-mark'));
      card.appendChild(art);

      const text = div('starter-text');
      const nameRow = div('starter-nameline');
      nameRow.appendChild(span('starter-name', name));
      text.appendChild(nameRow);
      const tags = div('starter-tags');
      tags.appendChild(span('starter-verb', s.verb));
      tags.appendChild(span('starter-dps', STARTER_DPS));
      tags.appendChild(span('starter-hullclass', ''));
      text.appendChild(tags);
      card.appendChild(text);

      card.addEventListener('click', () => this.select(i));
      cards.appendChild(card);
      this.cards.push(card);
    }
    this.root.appendChild(cards);

    // Detail below the selector, not beside it — the same shape every other row on this
    // screen uses at 390 px (AMD-1).
    this.detail = div('starter-detail');
    this.detail.setAttribute('aria-live', 'polite');
    this.root.appendChild(this.detail);
    this.shape = div('starter-shape');
    this.root.appendChild(this.shape);

    const stats = div('starter-stats');
    this.aim = statCell(stats, 'aim');
    this.reach = statCell(stats, 'reach');
    this.cost = statCell(stats, 'field cost');
    this.root.appendChild(stats);

    this.root.appendChild(
      div(
        'starter-note',
        'Matched damage by design. What changes is where it goes, how close you stand, what it costs the field — and the hull it builds.',
      ),
    );

    this.root.addEventListener('nav-next', () => this.select(this.index + 1));
    this.root.addEventListener('nav-prev', () => this.select(this.index - 1));
    this.render();
  }

  private select(i: number): void {
    const n = STARTERS.length;
    const next = ((i % n) + n) % n;
    if (next === this.index) return;
    this.index = next;
    this.render();
    this.onChange();
  }

  /** The rows no longer draw a hull, so a new seed changes nothing on them. */
  setSeed(seed: number): void {
    this.seed = seed >>> 0;
  }

  private render(): void {
    const s = STARTERS[this.index];
    for (let i = 0; i < this.cards.length; i++) {
      const on = i === this.index;
      setClass(this.cards[i], 'is-on', on);
      this.cards[i].setAttribute('aria-checked', String(on));
      const cls = this.cards[i].querySelector('.starter-hullclass');
      if (cls !== null) setText(cls, hullClassOf(starterTraitPoints(STARTERS[i].id)));
    }
    // §13.1.6's contract, borrowed: the sim owns the sentence that says what the weapon
    // does, and the UI renders it verbatim rather than re-deriving one that can drift.
    setText(this.detail, newWeaponDelta(s.id, 1));
    setText(this.shape, s.shape);
    setText(this.aim, s.aim);
    setText(this.reach, s.reach);
    setText(this.cost, s.cost);
    this.root.setAttribute('aria-label', 'Starting weapon: ' + this.cards[this.index].dataset.value);
  }

  get value(): string {
    return STARTERS[this.index].id;
  }
}

/** One `label / value` cell in the starter's three-stat strip. */
function statCell(parent: HTMLElement, label: string): HTMLElement {
  const cell = div('starter-stat');
  cell.appendChild(span('starter-stat-label', label));
  const v = span('starter-stat-value', '');
  cell.appendChild(v);
  parent.appendChild(cell);
  return v;
}

/**
 * Which of the three words the trait vector has landed on, or `MANTA` for a build that has
 * not committed. Derived from the points and nothing else, so the label cannot disagree with
 * the drawing beside it.
 */
function hullClassOf(points: readonly number[]): string {
  let best = 0;
  for (let t = 1; t < points.length; t++) if (points[t] > points[best]) best = t;
  return points[best] - TRAIT_BASELINE < 1 ? 'MANTA' : HULL_CLASS[best];
}

/**
 * The trait readout. Three rows, each a pip bar with the 4/4/4 baseline marked.
 *
 * A readout and not a stepper, and the copy says so once at the bottom rather than
 * apologising in every row. AMD-50's twelve points are re-derived from the owned build on
 * every `recomputeStats`, which is what makes pick order irrelevant and a snapshot restore
 * free; a pre-run allocation would be a second source of truth for the same twelve integers.
 */
class TraitReadout {
  readonly root: HTMLDivElement;
  private pips: HTMLElement[][] = [];
  private values: HTMLElement[] = [];

  constructor() {
    this.root = div('trait-readout');

    const head = div('col-head');
    head.appendChild(icon('shapes', 'carousel-emblem'));
    head.appendChild(div('carousel-label', 'Traits'));
    head.appendChild(span('starter-parity', TRAIT_TOTAL_POINTS + ' points · baseline ' + TRAIT_BASELINE));
    this.root.appendChild(head);

    for (let i = 0; i < TRAITS.length; i++) {
      const t = TRAITS[i];
      const row = div('trait-row');

      const line = div('trait-line');
      line.appendChild(icon(t.glyph, 'trait-glyph'));
      line.appendChild(span('trait-name', t.label));
      const bar = div('trait-bar');
      const cells: HTMLElement[] = [];
      for (let p = 0; p < TRAIT_PIPS; p++) {
        const pip = span('trait-pip');
        // The baseline mark. Not a colour: a rule down the left edge of the fifth pip, so
        // "above 4/4/4" and "below" are readable with the hue channel switched off (§13.4).
        if (p === TRAIT_BASELINE) pip.classList.add('is-baseline');
        bar.appendChild(pip);
        cells.push(pip);
      }
      this.pips.push(cells);
      line.appendChild(bar);
      const v = span('trait-value', '');
      this.values.push(v);
      line.appendChild(v);
      row.appendChild(line);

      row.appendChild(
        div('trait-note', 'per point ±' + TRAIT_PCT_PER_POINT + '% ' + t.stat + ' · pushes the hull toward ' + t.hull),
      );
      this.root.appendChild(row);
    }

    this.root.appendChild(
      div(
        'trait-foot',
        'Derived, not spent. The run re-derives all ' +
          TRAIT_TOTAL_POINTS +
          ' from what you own, every level-up — so this is where your opening puts them, not where they stay.',
      ),
    );
  }

  set(points: readonly number[]): void {
    for (let i = 0; i < TRAITS.length; i++) {
      const n = points[TRAITS[i].index];
      for (let p = 0; p < TRAIT_PIPS; p++) {
        const on = p < n;
        setClass(this.pips[i][p], 'is-on', on);
        // Above the baseline reads as a gain, below as a debt. Two shapes, not two hues:
        // a filled pip and a hollow one with a slash.
        setClass(this.pips[i][p], 'is-over', on && p >= TRAIT_BASELINE);
      }
      const d = n - TRAIT_BASELINE;
      setText(this.values[i], String(n) + (d === 0 ? '' : d > 0 ? ' (+' + d + ')' : ' (' + d + ')'));
      setClass(this.values[i], 'is-up', d > 0);
      setClass(this.values[i], 'is-down', d < 0);
    }
  }
}

/**
 * A `YOUR CAPTAIN` / `YOUR SHIP` identity card.
 *
 * One shape, two fillings: a framed piece of art on the left with a small corner tag and a
 * reroll, a text column on the right. Both are pure functions of `runSeed`, which is why
 * they share a card shape and why they share one reroll — see `LoadoutScreen.reroll`.
 */
class IdentityCard {
  readonly root: HTMLElement;
  readonly art: HTMLElement;
  readonly text: HTMLElement;
  readonly tag: HTMLElement;

  constructor(label: string, tagText: string, onReroll: () => void) {
    this.root = div('id-card');

    // The reroll sits in the card's head, not over its art. It overhung the portrait's
    // bottom-right corner on the first pass and covered a quarter of the face — which on a
    // feature whose whole bar is "you can say this captain's name an hour later" is the one
    // pixel it must never take.
    const head = div('id-head');
    head.appendChild(div('id-label', label));
    const roll = button('', 'id-reroll', 'refresh');
    roll.setAttribute('aria-label', 'Reroll the seed — new captain, new ship, new run');
    roll.title = 'New seed';
    roll.addEventListener('click', onReroll);
    head.appendChild(roll);
    this.root.appendChild(head);

    const body = div('id-body');
    this.art = div('id-art');
    this.tag = span('id-tag', tagText);
    this.art.appendChild(this.tag);
    body.appendChild(this.art);

    this.text = div('id-text');
    body.appendChild(this.text);
    this.root.appendChild(body);
  }
}

class Carousel {
  readonly root: HTMLDivElement;
  private options: Option[];
  private index = 0;
  private prevBtn: HTMLButtonElement;
  private nextBtn: HTMLButtonElement;
  private nameNode: HTMLElement;
  private countNode: HTMLElement;
  private detailNode: HTMLElement;
  private lockNode: HTMLElement;
  private lockText: HTMLElement;
  private pips: HTMLElement[] = [];
  private faces: HTMLElement[] = [];
  private label: string;
  private onChange: () => void;

  constructor(kind: string, label: string, options: Option[], onChange: () => void) {
    this.options = options;
    this.label = label;
    this.onChange = onChange;
    this.root = div('carousel');
    this.root.dataset.navHorizontal = 'true';
    this.root.tabIndex = 0;
    this.root.setAttribute('role', 'group');

    // --- head: row emblem, label, "1 / 3" counter, position pips ---------------
    const head = div('carousel-head');
    head.appendChild(icon(LOADOUT_ROW_ICONS[kind], 'carousel-emblem'));
    head.appendChild(div('carousel-label', label));
    this.countNode = span('carousel-count', '');
    head.appendChild(this.countNode);
    const pipRow = div('carousel-pips');
    pipRow.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < options.length; i++) {
      const p = span('pip');
      this.pips.push(p);
      pipRow.appendChild(p);
    }
    head.appendChild(pipRow);
    this.root.appendChild(head);

    // --- bar: arrow, face (silhouette or glyph + name), arrow -----------------
    const bar = div('carousel-bar');
    this.prevBtn = button('', 'arrow', 'angle-left');
    this.prevBtn.setAttribute('aria-label', 'Previous ' + label);
    this.prevBtn.addEventListener('click', () => this.step(-1));
    this.nextBtn = button('', 'arrow', 'angle-right');
    this.nextBtn.setAttribute('aria-label', 'Next ' + label);
    this.nextBtn.addEventListener('click', () => this.step(1));

    const face = div('carousel-face');
    face.setAttribute('aria-live', 'polite');
    const thumb = div('carousel-thumb');
    for (let i = 0; i < options.length; i++) {
      const art = div('carousel-art');
      const o = options[i];
      if (o.plan !== undefined) art.appendChild(arenaSilhouette(o.plan));
      else if (o.glyph !== '') art.appendChild(icon(o.glyph, 'carousel-glyph'));
      art.hidden = true;
      this.faces.push(art);
      thumb.appendChild(art);
    }
    thumb.appendChild(icon('lock', 'carousel-lockmark'));
    face.appendChild(thumb);
    this.nameNode = span('carousel-name', options[0].label);
    face.appendChild(this.nameNode);

    bar.appendChild(this.prevBtn);
    bar.appendChild(face);
    bar.appendChild(this.nextBtn);
    this.root.appendChild(bar);

    this.detailNode = div('carousel-detail', options[0].detail);
    this.root.appendChild(this.detailNode);

    this.lockNode = div('carousel-lock');
    this.lockNode.appendChild(icon('lock'));
    this.lockText = span('carousel-lock-text', '');
    this.lockNode.appendChild(this.lockText);
    this.lockNode.hidden = true;
    this.root.appendChild(this.lockNode);

    // A single-entry list is inert, and says so rather than swallowing the tap.
    const inert = options.length < 2;
    setArrowEnabled(this.prevBtn, !inert);
    setArrowEnabled(this.nextBtn, !inert);

    this.root.addEventListener('nav-next', () => this.step(1));
    this.root.addEventListener('nav-prev', () => this.step(-1));
    this.render();
  }

  /** Plain wrap-around over every entry — locked ones included (AMD-25). */
  private step(dir: number): void {
    const n = this.options.length;
    if (n < 2) return;
    this.index = (this.index + dir + n) % n;
    this.render();
    this.onChange();
  }

  private render(): void {
    const o = this.options[this.index];
    setText(this.nameNode, o.label);
    setText(this.detailNode, o.detail);
    setText(this.countNode, String(this.index + 1) + ' / ' + this.options.length);
    for (let i = 0; i < this.faces.length; i++) setHidden(this.faces[i], i !== this.index);
    for (let i = 0; i < this.pips.length; i++) {
      const p = this.pips[i];
      p.classList.toggle('is-on', i === this.index);
      p.classList.toggle('is-locked', this.options[i].locked);
    }
    this.root.classList.toggle('is-locked', o.locked);
    setHidden(this.lockNode, !o.locked);
    if (o.locked) setText(this.lockText, o.unlock_text);
    this.root.setAttribute(
      'aria-label',
      this.label + ': ' + o.label + (o.locked ? ' (locked — ' + o.unlock_text + ')' : ''),
    );
  }

  get value(): string {
    return this.options[this.index].id;
  }

  get current(): Option {
    return this.options[this.index];
  }

  get isLocked(): boolean {
    return this.options[this.index].locked;
  }

  get rowLabel(): string {
    return this.label;
  }

  setUnlocked(ids: readonly string[]): void {
    for (let i = 0; i < this.options.length; i++) {
      const o = this.options[i];
      if (o.unlock_text === '') continue;
      o.locked = ids.indexOf(o.id) < 0;
    }
    if (this.options[this.index].locked) this.index = 0;
    this.render();
    this.onChange();
  }
}

export class LoadoutScreen extends Screen {
  private starter: StarterPicker;
  private traits: TraitReadout;
  private arena: Carousel;
  private difficulty: Carousel;
  private policy: Carousel;
  private active: Carousel;
  private seedInput: HTMLInputElement;
  private seedError: HTMLElement;
  private gateNode: HTMLElement;
  private beginBtn: HTMLButtonElement;
  private seed = 0;
  /** AMD-54 — the captain's and the hull's own seeds. Both track `seed` until rerolled. */
  private captainSeed = 0;
  private hullSeed = 0;
  private blocked = false;

  // --- the two seed-derived cards ---
  private captainCard: IdentityCard;
  private shipCard: IdentityCard;
  private shipPreview: HullPreview;
  private faceCanvas: HTMLCanvasElement;
  private portrait: Portrait | null = null;
  private capName: HTMLElement;
  private capSub: HTMLElement;
  private capBoon: HTMLElement;
  private capBane: HTMLElement;
  private capFlavour: HTMLElement[] = [];
  private shipStat: HTMLElement;
  private shipFlavour: HTMLElement[] = [];

  constructor(emit: ActionSink, initialSeed: number, defaultPolicy: string) {
    super('loadout', emit);
    this.seed = initialSeed >>> 0;
    this.captainSeed = this.seed;
    this.hullSeed = this.seed;

    const head = div('screen-head');
    const title = div('screen-title');
    title.appendChild(icon('flag', 'screen-title-mark'));
    title.appendChild(span('', 'SHIP LOADOUT'));
    head.appendChild(title);
    const back = button('Back', 'ghost', 'angle-left');
    back.addEventListener('click', () => this.emit({ type: 'goto', screen: 'title' }));
    head.appendChild(back);
    this.root.appendChild(head);

    this.root.appendChild(
      div('loadout-sub', 'Traits reshape the hull: Swift flies a dart, Bulwark a barge, Striker a blade.'),
    );

    // --- row 1: who you are and what you fly, both drawn from the seed ---------
    const identity = div('loadout-identity');

    this.captainCard = new IdentityCard('Your Captain', '', () => this.rerollCaptain());
    this.faceCanvas = el('canvas', 'id-face');
    this.faceCanvas.setAttribute('aria-hidden', 'true');
    this.captainCard.art.insertBefore(this.faceCanvas, this.captainCard.art.firstChild);
    this.capName = div('id-name', '');
    this.capSub = div('id-sub', '');
    this.capBoon = div('id-trait is-up', '');
    this.capBane = div('id-trait is-down', '');
    this.captainCard.text.appendChild(this.capName);
    this.captainCard.text.appendChild(this.capSub);
    this.captainCard.text.appendChild(this.capBoon);
    this.captainCard.text.appendChild(this.capBane);
    for (let i = 0; i < 2; i++) {
      const f = div('id-flavour', '');
      this.capFlavour.push(f);
      this.captainCard.text.appendChild(f);
    }
    identity.appendChild(this.captainCard.root);

    this.shipCard = new IdentityCard('Your Ship', 'MANTA', () => this.rerollShip());
    this.shipPreview = new HullPreview('id-hull');
    this.shipCard.art.insertBefore(this.shipPreview.canvas, this.shipCard.art.firstChild);
    this.shipStat = div('id-name id-name-small', '');
    this.shipCard.text.appendChild(this.shipStat);
    const hardpoints = div('id-sub', 'Hardpoints 1 / 4 · slot 0 owns the upper wingtip');
    this.shipCard.text.appendChild(hardpoints);
    for (let i = 0; i < 2; i++) {
      const f = div('id-flavour', '');
      this.shipFlavour.push(f);
      this.shipCard.text.appendChild(f);
    }
    identity.appendChild(this.shipCard.root);
    this.root.appendChild(identity);

    // --- row 2: the two choices that are yours -------------------------------
    const cols = div('loadout-cols');
    const gate = (): void => this.refreshGate();

    this.starter = new StarterPicker(() => {
      this.refreshIdentity();
      gate();
    });
    cols.appendChild(this.starter.root);

    this.traits = new TraitReadout();

    /*
     * The right column: the trait readout and the four run settings, stacked.
     *
     * The weapon picker is three cards, a sentence and a stat table — about 600 px — and
     * the readout is 180. Left as two top-level blocks the readout finished a third of the
     * way down and the run settings started below the picker, so desktop drew a 250 px hole
     * under the traits and then a second one beside `Active Ability`. Nesting spends the
     * hole on the settings and keeps the linear order a phone reads identical.
     */
    const side = div('loadout-side');
    side.appendChild(this.traits.root);
    cols.appendChild(side);
    this.root.appendChild(cols);

    // --- the four run settings ------------------------------------------------
    const rows = div('loadout-rows');
    rows.appendChild(div('loadout-section', 'Run setup'));

    this.arena = new Carousel('arena', 'Arena', ARENAS.map(clone), gate);
    rows.appendChild(this.arena.root);

    this.difficulty = new Carousel('difficulty', 'Difficulty', difficultyOptions(), gate);
    rows.appendChild(this.difficulty.root);

    this.policy = new Carousel('policy', 'Targeting Policy', policyOptions(defaultPolicy), gate);
    rows.appendChild(this.policy.root);

    this.active = new Carousel('active', 'Active Ability', ACTIVES.map(clone), gate);
    rows.appendChild(this.active.root);

    side.appendChild(rows);

    // --- footer: seed + launch ---
    const footer = div('loadout-footer');
    const seedRow = div('seed-row');
    seedRow.appendChild(div('seed-label', 'Seed'));
    this.seedInput = el('input', 'seed-input');
    this.seedInput.type = 'text';
    this.seedInput.inputMode = 'numeric';
    this.seedInput.autocomplete = 'off';
    this.seedInput.spellcheck = false;
    this.seedInput.value = formatSeed(this.seed);
    this.seedInput.setAttribute('aria-label', 'Run seed');
    this.seedInput.addEventListener('input', () => {
      if (this.validateSeed()) this.refreshIdentity();
    });
    seedRow.appendChild(this.seedInput);

    const dice = button('', 'arrow', 'shuffle');
    dice.setAttribute('aria-label', 'Random seed');
    dice.addEventListener('click', () => this.reroll());
    seedRow.appendChild(dice);
    footer.appendChild(seedRow);

    footer.appendChild(
      div('seed-note', 'The seed makes the run. Each card rerolls only itself; the share string carries all three.'),
    );

    this.seedError = div('seed-error', 'Seed must be a number, 0x hex, or an LA1 share string.');
    this.seedError.hidden = true;
    footer.appendChild(this.seedError);

    this.gateNode = div('loadout-gate');
    this.gateNode.appendChild(icon('lock'));
    this.gateNode.appendChild(span('loadout-gate-text', ''));
    this.gateNode.hidden = true;
    footer.appendChild(this.gateNode);

    this.beginBtn = button('Launch', 'primary', 'play');
    this.beginBtn.addEventListener('click', () => {
      if (this.blocked) {
        shake(this.beginBtn);
        return;
      }
      if (!this.validateSeed()) {
        shake(this.seedInput);
        return;
      }
      this.emit({ type: 'start_run', loadout: this.selection() });
    });
    footer.appendChild(this.beginBtn);
    this.root.appendChild(footer);

    this.stops = [this.starter.root, this.arena.root, this.difficulty.root, this.policy.root, this.active.root, this.seedInput, dice, this.beginBtn, back];

    /*
     * The two identity rerolls were missing from that list, so a keyboard or gamepad player
     * could not reroll their captain or their ship — `nav.setStops()` is the only route in,
     * and a control absent from it renders perfectly and cannot be reached.
     *
     * Picked up from the DOM rather than appended by name, for the same reason the settings
     * screen now does it: a hand-maintained stop list drifts from the markup it describes,
     * and this is what that drift looks like. The explicit entries above keep their order;
     * this only adds what the list forgot.
     */
    const missed: HTMLElement[] = [];
    for (const el of this.root.querySelectorAll<HTMLElement>('button')) {
      if (this.stops.includes(el)) continue;
      if (this.stops.some((s) => s.contains(el))) continue;
      if (missed.some((m) => m.contains(el))) continue;
      missed.push(el);
    }
    this.stops = [...missed, ...this.stops];
    this.refreshGate();
    this.refreshIdentity();
  }

  /**
   * AMD-54 — three rerolls, and how the share string survives them.
   *
   * These two buttons used to be one act: both rolled `runSeed`, so asking for a different
   * face also threw away the hull you had settled on and the run behind it. The reason was
   * §13.1.10 — one string has to reproduce somebody's trench, their ship and their captain,
   * and a face rolled off the seed would be the first thing a share string could not
   * reproduce. That reason survives; the coupling does not. The captain and the hull each
   * carry their own seed now, the share string carries all three, and pasting a bare seed
   * still sets all three to it — which is exactly what every string written before today
   * means when it is read back.
   */
  private rerollCaptain(): void {
    this.captainSeed = randomSeed();
    this.refreshIdentity();
  }

  private rerollShip(): void {
    this.hullSeed = randomSeed();
    this.refreshIdentity();
  }

  /** The dice beside the seed field: a whole new run, which is all three at once. */
  private reroll(): void {
    this.seed = randomSeed();
    this.captainSeed = this.seed;
    this.hullSeed = this.seed;
    this.seedInput.value = formatSeed(this.seed);
    this.validateSeed();
    this.refreshIdentity();
    flash(this.seedInput);
  }

  /** Repaints everything the seed and the starter decide. Never called per frame. */
  private refreshIdentity(): void {
    const seed = this.seed >>> 0;
    const starterId = this.starter.value;
    const form = starterShipForm(starterId);
    const points = starterTraitPoints(starterId);

    this.starter.setSeed(seed);
    this.shipPreview.set(this.hullSeed >>> 0, form, { hot: true });
    this.traits.set(points);

    const cls = hullClassOf(points);
    setText(this.shipCard.tag, cls);
    setText(this.shipStat, cls + ' HULL');
    const def = weaponById.get(starterId);
    setText(
      this.shipFlavour[0],
      'Trait vector ' + points[TRAIT_SPEED] + ' / ' + points[TRAIT_DEFENCE] + ' / ' + points[TRAIT_ATTACK] +
        ' — swift / bulwark / striker.',
    );
    setText(
      this.shipFlavour[1],
      (def === undefined ? 'Slot 0' : def.display_name) + ' cuts the ' +
        glyphWordFor(form.slotGlyph[0]) + ' into the upper wingtip.',
    );

    const captain = makeCaptain(this.captainSeed >>> 0);
    this.paintCaptain(captain);
  }

  /**
   * The captain, at `CARD_SCALE`. The same path the death card uses — `composePortrait`
   * runs the shipped post chain over a 64×64 of ramp indices, so this face and the one on
   * the death card are the same face by construction and not by convention.
   *
   * One buffer for the session: `composePortrait` allocates, `recompose` does not, and a
   * seed field the player is typing into would otherwise allocate on every keystroke.
   */
  private paintCaptain(c: Captain): void {
    if (this.portrait === null) this.portrait = composePortrait(c, { scale: CARD_SCALE });
    else if (this.portrait.seed !== c.runSeed) recompose(c, this.portrait);
    paintPortraitToCanvas(this.portrait, this.faceCanvas);

    setText(this.capName, c.name.full.toUpperCase());
    const pron = c.pronouns.subject + '/' + c.pronouns.possessive;
    setText(this.capSub, (pron + ' · ' + c.temperament.label + ' · ' + c.traits.weight.label).toUpperCase());

    // The signed pair, verbatim. `display` already carries its own leading sign and is the
    // string `src/captain/traits.ts` derives the sim's ADD-bucket entry from, so printing it
    // rather than reassembling it from `stat` and `magnitude` is what keeps the card
    // incapable of describing a bonus the run will not grant.
    setText(this.capBoon, c.traits.boon.display);
    setText(this.capBane, c.traits.flaw.display);
    setHidden(this.capBoon, c.traits.boon.display === '');
    setHidden(this.capBane, c.traits.flaw.display === '');

    // Two flavour lines, from the trait block rather than from the temperament table: `[0]`
    // is drawn from this captain's own temperament pool and `[1]` from the shared one, so
    // the pair is specific to the person instead of being the same two sentences every
    // captain of that temperament prints.
    setText(this.capFlavour[0], c.traits.flavour[0]);
    setText(this.capFlavour[1], c.traits.flavour[1]);
    this.captainCard.root.setAttribute(
      'aria-label',
      'Your captain: ' + c.name.full + ', ' + c.temperament.label + '. ' +
        c.traits.boon.display + '. ' + c.traits.flaw.display + '.',
    );
    setText(this.captainCard.tag, c.temperament.label.toUpperCase());
  }

  /**
   * Begin Descent parks while any row rests on a locked entry, and the footer names
   * the row and its unlock condition. `aria-disabled` rather than `disabled`: the
   * button has to stay focusable or gamepad nav loses a stop (§13.4).
   */
  private refreshGate(): void {
    const rows: readonly Carousel[] = [this.arena, this.difficulty, this.policy, this.active];
    let reason = '';
    for (let i = 0; i < rows.length; i++) {
      if (!rows[i].isLocked) continue;
      // The row already prints the how; the footer only names the blocker.
      reason = rows[i].current.label + ' is locked — pick another ' + rows[i].rowLabel.toLowerCase() + '.';
      break;
    }
    this.blocked = reason !== '';
    this.beginBtn.classList.toggle('is-disabled', this.blocked);
    this.beginBtn.setAttribute('aria-disabled', String(this.blocked));
    setHidden(this.gateNode, !this.blocked);
    if (this.blocked) setText(this.gateNode.lastChild as Node, reason);
  }

  /** Accepts decimal, `0x` hex, or a full `LA1:` share string (§13.7 OQ-13.2). */
  private validateSeed(): boolean {
    const parsed = parseSeed(this.seedInput.value);
    const ok = parsed !== null;
    this.seedInput.classList.toggle('is-invalid', !ok);
    this.seedInput.setAttribute('aria-invalid', String(!ok));
    this.seedError.hidden = ok;
    if (ok && parsed !== null && parsed !== this.seed) {
      // Typing or pasting a seed re-couples all three. §13.1.10's round-trip is the point:
      // a bare seed has to mean the same run it meant before rerolls existed. A full AMD-54
      // share string is the one input that can say otherwise, so it is read first.
      const share = parseShareString(this.seedInput.value);
      this.seed = parsed;
      this.captainSeed = share?.captain_seed ?? parsed;
      this.hullSeed = share?.hull_seed ?? parsed;
    }
    return ok;
  }

  selection(): LoadoutSelection {
    return {
      arena_id: this.arena.value,
      difficulty_id: this.difficulty.value,
      policy: this.policy.value,
      active_id: this.active.value,
      seed: this.seed >>> 0,
      starter_weapon: this.starter.value,
      captain_seed: this.captainSeed >>> 0,
      hull_seed: this.hullSeed >>> 0,
    };
  }

  setSeed(seed: number): void {
    this.seed = seed >>> 0;
    this.captainSeed = this.seed;
    this.hullSeed = this.seed;
    this.seedInput.value = formatSeed(this.seed);
    this.validateSeed();
    this.refreshIdentity();
  }

  setUnlocks(unlocks: { arenas: readonly string[]; diff: readonly string[]; actives: readonly string[] }): void {
    this.arena.setUnlocked(unlocks.arenas);
    this.difficulty.setUnlocked(unlocks.diff);
    this.active.setUnlocked(unlocks.actives);
  }

  /** A hidden canvas measures 0×0, so the identity previews are repainted on the way in. */
  override onShow(): void {
    this.refreshIdentity();
  }

  override onBack(): boolean {
    this.emit({ type: 'goto', screen: 'title' });
    return true;
  }
}

/** The four glyph classes AMD-50 splices into the outline, as a word a card can print. */
function glyphWordFor(index: number): string {
  const words = ['spike', 'fork', 'blister', 'plate'];
  return index >= 0 && index < words.length ? words[index] : 'nothing';
}

function shake(node: HTMLElement): void {
  node.classList.remove('shake');
  void node.offsetWidth;
  node.classList.add('shake');
}

/** A one-shot accent pulse. The seed field, when a reroll somewhere else changed it. */
function flash(node: HTMLElement): void {
  node.classList.remove('flash');
  void node.offsetWidth;
  node.classList.add('flash');
}

function setArrowEnabled(b: HTMLButtonElement, on: boolean): void {
  b.disabled = !on;
  b.setAttribute('aria-disabled', String(!on));
}

/**
 * §10.8 geometry as a 24-unit silhouette: bare bounds for `trench-floor`, nine
 * chimneys for `smoker-gardens`, two three-gap ridge walls for `hadal-gate`. Locked
 * entries render the same drawing in the dim chrome tone, so the shape of the place
 * is legible before it is earned.
 */
function arenaSilhouette(plan: ArenaPlan): SVGSVGElement {
  const svg = svgTag('svg', {
    class: 'ic arena-plan',
    viewBox: '0 0 24 24',
    'aria-hidden': 'true',
    focusable: 'false',
  });
  svg.appendChild(
    svgTag('rect', { x: '2', y: '2', width: '20', height: '20', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }),
  );
  // Spawn mark at (0,0). Shared by all three plans, and it keeps the bare
  // `trench-floor` bounds from reading as an empty checkbox.
  svg.appendChild(svgTag('rect', { x: '11', y: '11', width: '2', height: '2', fill: 'currentColor' }));
  if (plan === 'chimneys') {
    // Nine, scattered rather than gridded (§10.8 seeds them by Poisson) and none
    // near the centre, which the same rule keeps clear for the spawn.
    const at = [
      [5, 5],
      [11, 4],
      [17, 6],
      [4, 11],
      [18, 12],
      [8, 16],
      [14, 15],
      [5, 19],
      [17, 18],
    ];
    for (let i = 0; i < at.length; i++) {
      svg.appendChild(svgTag('rect', { x: String(at[i][0]), y: String(at[i][1]), width: '2', height: '2', fill: 'currentColor' }));
    }
  } else if (plan === 'ridges') {
    const seg = [
      [4, 3],
      [9, 3],
      [14, 3],
      [18, 3],
    ];
    const ys = [7, 15];
    for (let y = 0; y < ys.length; y++) {
      for (let s = 0; s < seg.length; s++) {
        svg.appendChild(
          svgTag('rect', { x: String(seg[s][0]), y: String(ys[y]), width: String(seg[s][1]), height: '2', fill: 'currentColor' }),
        );
      }
    }
  }
  return svg;
}

function clone(o: Option): Option {
  return { id: o.id, label: o.label, detail: o.detail, locked: o.locked, unlock_text: o.unlock_text, glyph: o.glyph, plan: o.plan };
}

function difficultyOptions(): Option[] {
  const out: Option[] = [
    {
      id: 'diff.standard',
      label: 'Standard',
      detail: 'The trench as designed. No multipliers.',
      locked: false,
      unlock_text: '',
      glyph: DIFFICULTY_ICONS['diff.standard'],
    },
  ];
  for (let i = 0; i < DIFFICULTY_TIERS.length; i++) {
    const t = DIFFICULTY_TIERS[i] as Record<string, unknown>;
    const mult = t.stat_multipliers as Record<string, number> | undefined;
    const hp = mult && typeof mult.enemy_hp === 'number' ? mult.enemy_hp : 1;
    const dmg = mult && typeof mult.enemy_damage === 'number' ? mult.enemy_damage : 1;
    const id = String(t.id);
    out.push({
      id,
      label: typeof t.display_name === 'string' ? t.display_name : humanizeId(id),
      detail: 'Enemies ×' + hp.toFixed(2) + ' health, ×' + dmg.toFixed(2) + ' damage.',
      locked: true,
      // §13.1.2: locked tiers show the unlock condition verbatim from data.
      unlock_text: typeof t.unlock_condition === 'string' ? t.unlock_condition : 'Locked',
      glyph: DIFFICULTY_ICONS[id] !== undefined ? DIFFICULTY_ICONS[id] : 'fire',
    });
  }
  return out;
}

function policyOptions(preferred: string): Option[] {
  const out: Option[] = [];
  for (let i = 0; i < TARGETING_POLICIES.length; i++) {
    const p = TARGETING_POLICIES[i];
    out.push({
      id: p.id,
      label: p.label,
      detail: p.blurb,
      locked: false,
      unlock_text: '',
      glyph: POLICY_ICONS[p.id] !== undefined ? POLICY_ICONS[p.id] : 'crosshair',
    });
  }
  const at = out.findIndex((o) => o.id === preferred);
  if (at > 0) {
    const [pick] = out.splice(at, 1);
    out.unshift(pick);
  }
  return out;
}

/**
 * Today's random seed for the loadout default. Presentation-side only: the value is
 * handed to the sim as an explicit run seed, so determinism is unaffected (spine §7).
 */
export function randomSeed(): number {
  return (Math.random() * 0x100000000) >>> 0;
}

/**
 * Guard for "the picker offers exactly what the sim will grant".
 *
 * `grantStarterWeapon` silently falls back to the Lance for an id it does not recognise,
 * which is the right behaviour for a stale save and the wrong one for a card the player
 * just tapped: they would pick the Halo and descend holding a Lance with nothing said.
 * Returns the offending ids; empty ⇒ the two tables agree.
 */
export function starterPickerProblems(): string[] {
  const problems: string[] = [];
  for (let i = 0; i < STARTERS.length; i++) {
    const id = STARTERS[i].id;
    if (STARTER_WEAPONS.indexOf(id) < 0) problems.push(id + ' is offered but is not a STARTER_WEAPONS entry');
    if (!weaponById.has(id)) problems.push(id + ' is offered but is not in weapons.json');
  }
  for (let i = 0; i < STARTER_WEAPONS.length; i++) {
    const id = STARTER_WEAPONS[i];
    let found = false;
    for (let k = 0; k < STARTERS.length; k++) if (STARTERS[k].id === id) found = true;
    if (!found) problems.push(id + ' is a starter the picker does not offer');
  }
  return problems;
}

/**
 * Guard for "every trait row names a trait the sim has".
 *
 * The three rows are indexed by `TRAIT_SPEED` / `TRAIT_DEFENCE` / `TRAIT_ATTACK`, and the
 * per-point line quotes `TRAIT_PCT_PER_POINT`. What this catches is the readout drifting out
 * of `ship-form.json`: a pool that stops being twelve, or a baseline that stops being four,
 * leaves the pip bar describing a scale that no longer exists.
 */
export function traitReadoutProblems(): string[] {
  const problems: string[] = [];
  const pb = SHIP_FORM.point_buy;
  if (pb.total_points !== TRAIT_TOTAL_POINTS) problems.push('trait pool is not TRAIT_TOTAL_POINTS');
  if (pb.baseline_per_trait !== TRAIT_BASELINE) problems.push('baseline is not TRAIT_BASELINE');
  if (TRAIT_BASELINE * 3 !== TRAIT_TOTAL_POINTS) problems.push('three baselines no longer spend the pool');
  if (TRAIT_PIPS < TRAIT_BASELINE + 1) problems.push('pip bar cannot show the baseline');
  const seen = new Set<number>();
  for (let i = 0; i < TRAITS.length; i++) {
    if (seen.has(TRAITS[i].index)) problems.push(TRAITS[i].label + ' repeats a trait index');
    seen.add(TRAITS[i].index);
  }
  if (seen.size !== 3) problems.push('the readout does not cover all three traits');
  return problems;
}
